import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Hash, Link2, Shield, Phone, Plus, Trash2, ArrowRight, CheckCircle2, DownloadCloud, Smartphone } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";

export default function Onboarding() {
  const { t, user, setUser, formatApiError } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [carName, setCarName] = useState("My Car");
  const [plate, setPlate] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [contacts, setContacts] = useState([]);
  const [cName, setCName] = useState("");
  const [cPhone, setCPhone] = useState("");
  const [cChatId, setCChatId] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const total = 3;

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const addContact = async (e) => {
    e.preventDefault();
    setErr("");
    if (!cName || cPhone.length < 7 || !cChatId) return;
    try {
      await api.post("/contacts", { name: cName, phone: cPhone, telegram_chat_id: cChatId });
      setCName("");
      setCPhone("");
      setCChatId("");
      const { data } = await api.get("/contacts");
      setContacts(data);
    } catch (er) { setErr(formatApiError(er)); }
  };
  const removeContact = async (id) => {
    await api.delete(`/contacts/${id}`);
    const { data } = await api.get("/contacts");
    setContacts(data);
  };

  const saveCarAndDevice = async () => {
    setErr(""); setSaving(true);
    try {
      await api.post("/device/link", {
        device_id: deviceId.trim(),
        name: carName.trim() || "My Car",
        plate: plate.trim().toUpperCase() || null,
      });
      return true;
    } catch (er) {
      setErr(formatApiError(er));
      return false;
    } finally { setSaving(false); }
  };

  const finish = async () => {
    try {
      const { data } = await api.patch("/auth/me", { onboarded: true });
      setUser(data);
    } catch (_) {}
    nav("/home", { replace: true });
  };

  return (
    <div data-testid="onboarding-screen" className="min-h-[100dvh] bg-white flex flex-col">
      <div className="px-6 pt-8 pb-4 border-b border-slate-100">
        <div className="text-xs font-bold uppercase tracking-wider text-primary">
          {t.onbStep.replace("{n}", step + 1).replace("{total}", total)}
        </div>
        <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((step + 1) / total) * 100}%` }}
          />
        </div>
      </div>

      <div className="flex-1 px-6 py-6 overflow-auto no-scrollbar pb-28">
        {step === 0 && (
          <div data-testid="onb-step-car" className="animate-fadeIn">
            <div className="w-14 h-14 rounded-2xl bg-primary-light text-primary flex items-center justify-center">
              <Car size={26}/>
            </div>
            <h1 className="mt-4 text-2xl font-black text-slate-900">{t.onbCarTitle}</h1>
            <p className="text-sm text-slate-500 mt-1">{t.onbCarBody}</p>
            <div className="mt-6 space-y-4">
              <Field label={t.carName} icon={<Car size={16}/>}>
                <input
                  data-testid="onb-car-name"
                  value={carName}
                  onChange={(e) => setCarName(e.target.value)}
                  placeholder="e.g. Maruti Swift"
                  className="flex-1 outline-none"
                />
              </Field>
              <Field label={t.numberPlate} icon={<Hash size={16}/>}>
                <input
                  data-testid="onb-plate"
                  value={plate}
                  onChange={(e) => setPlate(e.target.value.toUpperCase())}
                  placeholder="DL 01 AB 1234"
                  className="flex-1 outline-none font-mono tracking-wider"
                />
              </Field>
            </div>

            {/* APK Download Section */}
            <div className="mt-6 rounded-2xl border-2 border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shrink-0">
                  <Smartphone size={20} />
                </div>
                <div>
                  <p className="font-black text-slate-900 text-sm">Install the Head Unit App First</p>
                  <p className="text-xs text-slate-500 mt-0.5">Required before Step 2</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                In <strong>Step 2</strong>, you'll need a <strong>Device ID</strong> (e.g. <span className="font-mono bg-white px-1.5 py-0.5 rounded text-primary font-bold text-xs">CT-XXXX-XXXX</span>). This ID is displayed on screen after installing the CarTrack APK on your car's Android head unit.
              </p>
              <a
                href="/app-release.apk"
                download="cartrack-headunit.apk"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary text-white font-bold text-sm active:scale-[0.98] transition-transform"
              >
                <DownloadCloud size={16} />
                Download Car Head Unit APK
              </a>
              <p className="text-xs text-slate-400 text-center mt-2">
                Install on your car's Android screen, then continue to Step 2.
              </p>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="onb-step-device" className="animate-fadeIn">
            <div className="w-14 h-14 rounded-2xl bg-primary-light text-primary flex items-center justify-center">
              <Link2 size={26}/>
            </div>
            <h1 className="mt-4 text-2xl font-black text-slate-900">{t.onbDeviceTitle}</h1>
            <p className="text-sm text-slate-500 mt-1">{t.onbDeviceBody}</p>
            <Field label={t.deviceId} icon={<Link2 size={16}/>} className="mt-6">
              <input
                data-testid="onb-device-id"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value.trim())}
                placeholder="CT-XXXX-XXXX"
                className="flex-1 outline-none font-mono tracking-wider"
              />
            </Field>
            <p className="mt-2 text-xs text-slate-400">
              Don't have one yet? Use demo ID <span className="font-mono font-bold text-slate-600">CT-DEMO-0001</span>
            </p>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onb-step-contacts" className="animate-fadeIn">
            <div className="w-14 h-14 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center">
              <Shield size={26}/>
            </div>
            <h1 className="mt-4 text-2xl font-black text-slate-900">{t.onbContactsTitle}</h1>
            <p className="text-sm text-slate-500 mt-1">{t.onbContactsBody}</p>

            {contacts.length > 0 && (
              <ul className="mt-4 divide-y divide-slate-100">
                {contacts.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 py-2.5">
                    <div className="w-8 h-8 rounded-full bg-primary-light text-primary flex items-center justify-center">
                      <Phone size={14}/>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-sm">{c.name}</div>
                      <div className="text-xs font-mono text-slate-500">{c.phone}</div>
                    </div>
                    <button onClick={() => removeContact(c.id)} className="p-1.5 rounded-lg text-parked hover:bg-red-50">
                      <Trash2 size={14}/>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {contacts.length < 3 && (
              <form onSubmit={addContact} className="mt-4 space-y-2">
                <input
                  data-testid="onb-contact-name"
                  value={cName}
                  onChange={(e) => setCName(e.target.value)}
                  placeholder={t.contactName}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-primary text-sm"
                />
                <div className="flex items-center gap-2">
                  <input
                    data-testid="onb-contact-phone"
                    value={cPhone}
                    onChange={(e) => setCPhone(e.target.value.replace(/[^\d+]/g, ""))}
                    placeholder="e.g. +1234567890"
                    required
                    className="flex-1 border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-primary text-sm font-mono"
                  />
                </div>
                <input
                  data-testid="onb-contact-chatid"
                  value={cChatId}
                  onChange={(e) => setCChatId(e.target.value.replace(/[^\d-]/g, ""))}
                  placeholder="Telegram Chat ID"
                  required
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-primary text-sm font-mono"
                />
                <button
                  data-testid="onb-contact-add"
                  disabled={!cName || cPhone.length < 7 || !cChatId}
                  className="w-full py-2 rounded-xl bg-primary-light text-primary font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1"
                >
                  <Plus size={14}/> {t.addContact}
                </button>
              </form>
            )}
          </div>
        )}

        {err && <div className="mt-4 text-sm text-parked bg-red-50 rounded-lg px-3 py-2">{err}</div>}
      </div>

      {/* Sticky footer */}
      <div
        className="sticky bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center gap-2"
        style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
      >
        {step > 0 && (
          <button onClick={back} data-testid="onb-back" className="px-4 py-3 rounded-xl border border-slate-200 font-bold text-slate-600 text-sm">
            ← {t.back}
          </button>
        )}
        {step === 0 && (
          <button
            data-testid="onb-next"
            onClick={next}
            disabled={!carName}
            className="flex-1 py-3 rounded-xl bg-primary text-white font-bold text-sm active:scale-[0.98] disabled:opacity-60 inline-flex items-center justify-center gap-1"
          >
            {t.continueLbl} <ArrowRight size={16}/>
          </button>
        )}
        {step === 1 && (
          <button
            data-testid="onb-link-device"
            onClick={async () => { if (!deviceId) return; const ok = await saveCarAndDevice(); if (ok) next(); }}
            disabled={!deviceId || saving}
            className="flex-1 py-3 rounded-xl bg-primary text-white font-bold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-1"
          >
            {saving ? t.loading : t.continueLbl} <ArrowRight size={16}/>
          </button>
        )}
        {step === 2 && (
          <>
            <button data-testid="onb-skip" onClick={finish} className="px-3 py-3 text-slate-500 font-bold text-sm">
              {t.skipForNow}
            </button>
            <button data-testid="onb-finish" onClick={finish} className="flex-1 py-3 rounded-xl bg-primary text-white font-bold text-sm inline-flex items-center justify-center gap-1">
              <CheckCircle2 size={16}/> {t.finish}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, icon, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <div className="mt-1 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-3 focus-within:border-primary">
        <span className="text-slate-400">{icon}</span>
        {children}
      </div>
    </label>
  );
}
